import React from "react";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import {
  Settings,
  Bookmark,
  Sparkles,
  Table2,
  Columns3,
  Rows3,
  CalendarDays,
} from "lucide-react";
import { ReportTypeItem, AxisChoice, CannedReport } from "../types";

interface ReportToolbarProps {
  reportTypes: ReportTypeItem[];
  axisChoices: AxisChoice[];
  selectedReportType: string;
  selectedXDimension: string;
  selectedYDimension: string;
  startDate: string;
  endDate: string;
  loading: boolean;
  configLoading: boolean;
  cannedReports: CannedReport[];
  onReportTypeChange: (type: string) => void;
  onXDimensionChange: (dim: string) => void;
  onYDimensionChange: (dim: string) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onGenerate: () => void;
  onBookmark: () => void;
  onLoadCanned: (canned: CannedReport) => void;
  onDeleteCanned: (id: number) => void;
  /** Show the admin Reports Settings gear (admins only). */
  showSettings?: boolean;
  onOpenSettings?: () => void;
}


const ReportToolbar: React.FC<ReportToolbarProps> = ({
  reportTypes,
  axisChoices,
  selectedReportType,
  selectedXDimension,
  selectedYDimension,
  startDate,
  endDate,
  loading,
  configLoading,
  cannedReports,
  onReportTypeChange,
  onXDimensionChange,
  onYDimensionChange,
  onStartDateChange,
  onEndDateChange,
  onGenerate,
  onBookmark,
  onLoadCanned,
  onDeleteCanned,
  showSettings,
  onOpenSettings,
}) => {
  const canGenerate =
    selectedReportType && selectedXDimension && selectedYDimension && !loading;
  const canBookmark = selectedReportType && selectedXDimension && selectedYDimension;

  return (
    <Card className="mb-3 gr-toolbar-card">
      <div className="gr-toolbar-accent" />
      <Card.Body style={{ padding: "16px 18px" }}>
        {/* Saved reports bar */}
        {cannedReports.length > 0 && (
          <div className="gr-saved-bar">
            <span className="gr-saved-label">
              <Bookmark size={13} /> Saved
            </span>
            {cannedReports.map((cr) => (
              <span
                key={cr.id}
                className="grid-report-pill"
                title={cr.description || cr.displayLabel}
                onClick={() => onLoadCanned(cr)}
              >
                {cr.displayLabel}
                <span
                  className="gr-pill-x"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteCanned(cr.id);
                  }}
                  title="Remove bookmark"
                >
                  &times;
                </span>
              </span>
            ))}
          </div>
        )}

        <div className="gr-toolbar-fields">
          <Form.Group className="gr-field gr-field-report">
            <label className="gr-field-label">
              <Table2 size={13} /> Report Type
            </label>
            <Form.Select
              value={selectedReportType}
              onChange={(e) => onReportTypeChange(e.target.value)}
              disabled={configLoading}
            >
              <option value="">— Select report —</option>
              {reportTypes.map((rt) => (
                <option key={rt.key} value={rt.key}>
                  {rt.displayName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="gr-field">
            <label className="gr-field-label">
              <Columns3 size={13} /> X Axis · Columns
            </label>
            <Form.Select
              value={selectedXDimension}
              onChange={(e) => onXDimensionChange(e.target.value)}
              disabled={axisChoices.length === 0}
            >
              <option value="">— Select —</option>
              {axisChoices.map((ac) => (
                <option key={ac.key} value={ac.key}>
                  {ac.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="gr-field">
            <label className="gr-field-label">
              <Rows3 size={13} /> Y Axis · Rows
            </label>
            <Form.Select
              value={selectedYDimension}
              onChange={(e) => onYDimensionChange(e.target.value)}
              disabled={axisChoices.length === 0}
            >
              <option value="">— Select —</option>
              {axisChoices.map((ac) => (
                <option key={ac.key} value={ac.key}>
                  {ac.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="gr-field gr-field-date">
            <label className="gr-field-label">
              <CalendarDays size={13} /> From
            </label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="gr-field gr-field-date">
            <label className="gr-field-label">
              <CalendarDays size={13} /> To
            </label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
            />
          </Form.Group>

          <div className="gr-toolbar-actions">
            <Button
              variant="primary"
              onClick={onGenerate}
              disabled={!canGenerate}
              className="gr-generate-btn"
            >
              {loading ? (
                <>
                  <Spinner role="status" animation="border" size="sm" style={{ marginRight: 8 }} />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles size={15} style={{ marginRight: 7 }} />
                  Generate
                </>
              )}
            </Button>

            <button
              type="button"
              className="gr-icon-btn"
              onClick={onBookmark}
              disabled={!canBookmark}
              title="Save this report configuration as a bookmark"
            >
              <Bookmark size={16} />
            </button>

            {showSettings && (
              <button
                type="button"
                className="gr-icon-btn"
                onClick={onOpenSettings}
                title="Reports Settings — display names, enable/disable, roles"
              >
                <Settings size={16} />
              </button>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ReportToolbar;
